// NotificationCard.js
import React, { useState } from 'react';

const NotificationCard = ({ notif, onDecision, user }) => {
  const isAccessRequest = notif.topic?.startsWith('request_access');
  const isFinalVideo = notif.topic?.startsWith('final_video_ready_');
  const isRead = notif.is_read === 1;

  const date = new Date(
    notif.created_at || notif.timestamp || Date.now()
  ).toLocaleString();

  const [showReasonInput, setShowReasonInput] = useState(false);
  const [markedAsRead, setMarkedAsRead] = useState(isRead);

  const handleMarkAsRead = () => {
    onDecision?.(notif, 'read');
    setMarkedAsRead(true);
  };

  // ===== Avatar / user =======================================================
  const avatarSrc =
    user?.avatar_url ||
    user?.profilepath ||
    '/default-avatar.png';

  const displayName =
    user?.display_name ||
    user?.username ||
    notif.user_id ||
    notif.decided_by ||
    'Utilisateur';

  // ===== URLs pour la vidéo finale / export ==================================
  const exportUrl =
    notif.action_url ||
    notif.actionUrl ||
    (notif.project_id ? `/project/${notif.project_id}/manage#export` : null);

  const videoUrl = notif.video_url || notif.videoUrl || null;

  return (
    <div
      className={`card mb-2 shadow-sm border-0 ${
        isAccessRequest
          ? 'border-start border-4 border-warning'
          : isFinalVideo
          ? 'border-start border-4 border-success'
          : 'border-start border-4 border-light'
      }`}
    >
      <div
        className={`card-body d-flex flex-column gap-2 ${
          markedAsRead ? 'bg-light' : ''
        }`}
      >
        {/* ====== HEADER : avatar + titre + date + bouton Lu =================== */}
        <div className="d-flex align-items-center gap-3">
          <img
            src={avatarSrc}
            alt={displayName}
            className="rounded-circle flex-shrink-0"
            width="44"
            height="44"
            style={{ objectFit: 'cover' }}
          />

          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {isAccessRequest ? (
                <>
                  <i className="bi bi-unlock-fill text-warning"></i>
                  <span className="fw-semibold">
                    Demande d&apos;accès
                  </span>
                </>
              ) : isFinalVideo ? (
                <>
                  <i className="bi bi-film text-success"></i>
                  <span className="fw-semibold">
                    {notif.title || 'Vidéo finale prête'}
                  </span>
                </>
              ) : (
                <>
                  <i className="bi bi-bell-fill text-primary"></i>
                  <span className="fw-semibold">
                    {notif.title || 'Notification'}
                  </span>
                </>
              )}

              {notif.project_id && (
                <span className="text-muted small">
                  — Projet #{notif.project_id}
                </span>
              )}
            </div>

            <div className="text-muted small d-flex align-items-center gap-2 flex-wrap">
              <i className="bi bi-person-circle" />
              <span>{displayName}</span>
              <span>·</span>
              <i className="bi bi-clock" />
              <span>{date}</span>
            </div>
          </div>

          {!markedAsRead && !notif.decision && (
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleMarkAsRead}
              title="Marquer comme lu"
            >
              <i className="bi bi-eye me-1"></i> Lu
            </button>
          )}
        </div>

        {/* ===== MESSAGE ======================================================= */}
        {notif.message && (
          <div className="text-muted small">
            {notif.message}
          </div>
        )}

        {/* ===== Actions spécifiques pour la vidéo finale ====================== */}
        {isFinalVideo && (exportUrl || videoUrl) && (
          <div className="mt-2 d-flex flex-wrap gap-2">
            {exportUrl && (
              <a
                className="btn btn-sm btn-outline-primary"
                href={exportUrl}
              >
                <i className="bi bi-box-arrow-up-right me-1" />
                Ouvrir la page d&apos;export
              </a>
            )}
            {videoUrl && (
              <a
                className="btn btn-sm btn-primary"
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-play-circle-fill me-1" />
                Lire la vidéo
              </a>
            )}
          </div>
        )}

        {/* ===== Compat legacy : anciennes notifs avec ?step=export =========== */}
        {!isFinalVideo &&
          typeof notif.message === 'string' &&
          /\/project\/(\d+)\?step=export/.test(notif.message) && (
            <div className="mt-1">
              <a
                className="btn btn-sm btn-outline-primary"
                href={
                  notif.message.match(/(\/project\/\d+\?step=export)/)?.[1] ||
                  '#'
                }
              >
                <i className="bi bi-box-arrow-up-right me-1" /> Ouvrir
                l&apos;export
              </a>
            </div>
          )}

        {/* ===== Zone décision / access request ================================ */}
        <div className="d-flex align-items-center justify-content-between mt-1">
          {notif.decision ? (
            <div>
              <span
                className={`badge ${
                  notif.decision === 'approved'
                    ? 'bg-success'
                    : 'bg-danger'
                }`}
              >
                <i
                  className={`bi ${
                    notif.decision === 'approved'
                      ? 'bi-check-circle'
                      : 'bi-x-circle'
                  } me-1`}
                ></i>
                {notif.decision === 'approved'
                  ? 'Approuvé'
                  : 'Refusé'}
              </span>
              {notif.reason && (
                <span className="text-muted small ms-2">
                  Motif : {notif.reason}
                </span>
              )}
            </div>
          ) : (
            isAccessRequest && (
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => onDecision?.(notif, 'approved')}
                >
                  <i className="bi bi-check-circle me-1"></i> Approuver
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setShowReasonInput(true)}
                >
                  <i className="bi bi-x-circle me-1"></i> Refuser
                </button>
              </div>
            )
          )}

          {showReasonInput && (
            <div className="w-100 mt-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Motif du refus"
                onBlur={(e) => {
                  const reason = e.target.value.trim();
                  if (reason) onDecision?.(notif, 'denied', reason);
                  setShowReasonInput(false);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;
